import React, { useState, useEffect } from 'react';
import { 
  MdCurrencyRupee, MdAccessTime, MdCancel, MdWarning,
  MdCheckCircle, MdPayment, MdSecurity, MdCreditCard,
  MdEmail, MdPhone, MdLocationOn, MdContactSupport,
  MdDownload, MdArrowForward, MdMenu, MdClose,
  MdInfo, MdCalendarToday, MdReceipt, MdAccountBalance,
  MdHelp, MdExpandMore, MdExpandLess
} from 'react-icons/md';
import { FaMoneyBillWave, FaRegClock, FaExclamationTriangle } from 'react-icons/fa';
import { RiRefund2Line, RiFilePaperLine } from 'react-icons/ri';
import '../style.css';

const RefundPolicy = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [expandedSections, setExpandedSections] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const sections = [
    { id: 'overview', title: 'Overview', icon: <RiFilePaperLine /> },
    { id: 'cancellation', title: 'Cancellation Windows', icon: <MdCalendarToday /> },
    { id: 'refund-amounts', title: 'Refund Amounts', icon: <FaMoneyBillWave /> },
    { id: 'refund-process', title: 'Refund Process', icon: <RiRefund2Line /> },
    { id: 'non-refundable', title: 'Non-Refundable', icon: <MdCancel /> },
    { id: 'contact', title: 'Contact', icon: <MdContactSupport /> }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setShowMobileMenu(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = isMobile ? 70 : 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const lastUpdated = new Date().toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="refund-page">
      {/* Mobile Header - Fixed at top */}
      <header className="mobile-header">
        <div className="mobile-header-content">
          <div className="logo-area">
            <div className="mobile-logo">
              <RiRefund2Line />
              <span>VNS Tours</span>
            </div>
          </div>
          
          <button 
            className={`mobile-menu-btn ${showMobileMenu ? 'active' : ''}`}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Toggle menu"
          >
            {showMobileMenu ? <MdClose size={24} /> : <MdMenu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu - Fixed position */}
      {showMobileMenu && (
        <div className="mobile-nav-overlay">
          <div className="mobile-nav-container">
            <div className="mobile-nav-header">
              <h3>Refund Policy</h3>
              <p>Navigate sections</p>
            </div>
            
            <nav className="mobile-nav-menu">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`mobile-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  <span className="nav-icon">{section.icon}</span>
                  <span className="nav-text">{section.title}</span>
                  <MdArrowForward className="nav-arrow" />
                </button>
              ))}
            </nav>
            
            <div className="mobile-nav-footer">
              <button className="mobile-download-btn">
                <MdDownload /> Download Policy
              </button>
              <button className="mobile-contact-btn">
                <MdContactSupport /> Contact Support
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Adjusted for mobile header */}
      <main className="refund-content">
        {/* Hero Section */}
        <section className="refund-hero">
          <div className="hero-content">
            <div className="hero-icon">
              <RiRefund2Line className="hero-main-icon" />
              <FaMoneyBillWave className="hero-sec-icon" />
            </div>
            <h1 className="hero-title">
              <RiRefund2Line /> Refund Policy
            </h1>
            <p className="hero-subtitle">
              Fair & transparent refund policy for all bookings
            </p>
            
            <div className="hero-info">
              <div className="info-item">
                <MdAccessTime />
                <div>
                  <span className="info-label">Last Updated</span>
                  <span className="info-value">{lastUpdated}</span>
                </div>
              </div>
              
              <div className="info-item">
                <MdCurrencyRupee />
                <div>
                  <span className="info-label">Refund Time</span>
                  <span className="info-value">7-10 Days</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Policy Sections - Mobile Optimized */}
        <div className="policy-sections">
          
          {/* Overview Section */}
          <section id="overview" className="policy-card">
            <div 
              className="card-header"
              onClick={() => isMobile && toggleSection('overview')}
            >
              <div className="header-left">
                <div className="card-icon">
                  <RiFilePaperLine />
                </div>
                <h2>Policy Overview</h2>
              </div>
              {isMobile && (
                <span className="expand-icon">
                  {expandedSections.includes('overview') ? <MdExpandLess /> : <MdExpandMore />}
                </span>
              )}
            </div>
            
            <div className={`card-body ${isMobile && !expandedSections.includes('overview') ? 'collapsed' : ''}`}>
              <p className="card-text">
                We understand plans change. Our refund policy ensures fairness for all customers.
              </p>
              
              <div className="notice-card">
                <div className="notice-icon">
                  <MdWarning />
                </div>
                <div className="notice-content">
                  <h4>Important Notice</h4>
                  <p>Submit refund requests via email with your booking ID.</p>
                </div>
              </div>
              
              <div className="features-grid">
                <div className="feature">
                  <MdAccessTime />
                  <h5>Quick Processing</h5>
                  <p>7-10 business days</p>
                </div>
                
                <div className="feature">
                  <MdCurrencyRupee />
                  <h5>No Hidden Fees</h5>
                  <p>Transparent charges</p>
                </div>
                
                <div className="feature">
                  <MdSecurity />
                  <h5>Secure Refunds</h5>
                  <p>Safe processing</p>
                </div>
              </div>
            </div>
          </section>

          {/* Cancellation Windows */}
          <section id="cancellation" className="policy-card">
            <div 
              className="card-header"
              onClick={() => isMobile && toggleSection('cancellation')}
            >
              <div className="header-left">
                <div className="card-icon">
                  <MdCalendarToday />
                </div>
                <h2>Cancellation Windows</h2>
              </div>
              {isMobile && (
                <span className="expand-icon">
                  {expandedSections.includes('cancellation') ? <MdExpandLess /> : <MdExpandMore />}
                </span>
              )}
            </div>
            
            <div className={`card-body ${isMobile && !expandedSections.includes('cancellation') ? 'collapsed' : ''}`}>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="time-badge">30+ Days</div>
                  <div className="timeline-content">
                    <h5>Full Refund Window</h5>
                    <p>90% refund after 10% charge</p>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="time-badge">15-29 Days</div>
                  <div className="timeline-content">
                    <h5>Standard Window</h5>
                    <p>75% refund after 25% charge</p>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="time-badge">7-14 Days</div>
                  <div className="timeline-content">
                    <h5>Short Notice</h5>
                    <p>50% refund after 50% charge</p>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="time-badge last-minute">0-6 Days</div>
                  <div className="timeline-content">
                    <h5>Last Minute</h5>
                    <p className="warning-text">No refund available</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Refund Amounts - Mobile Table */}
          <section id="refund-amounts" className="policy-card">
            <div 
              className="card-header"
              onClick={() => isMobile && toggleSection('refund-amounts')}
            >
              <div className="header-left">
                <div className="card-icon">
                  <FaMoneyBillWave />
                </div>
                <h2>Refund Amounts</h2>
              </div>
              {isMobile && (
                <span className="expand-icon">
                  {expandedSections.includes('refund-amounts') ? <MdExpandLess /> : <MdExpandMore />}
                </span>
              )}
            </div>
            
            <div className={`card-body ${isMobile && !expandedSections.includes('refund-amounts') ? 'collapsed' : ''}`}>
              <div className="mobile-table">
                <div className="table-row header">
                  <div>Days Before</div>
                  <div>Refund %</div>
                  <div>Time</div>
                </div>
                
                <div className="table-row">
                  <div>30+ days</div>
                  <div className="refund-high">90%</div>
                  <div>3-5 days</div>
                </div>
                
                <div className="table-row">
                  <div>15-29 days</div>
                  <div className="refund-medium">75%</div>
                  <div>5-7 days</div>
                </div>
                
                <div className="table-row">
                  <div>7-14 days</div>
                  <div className="refund-low">50%</div>
                  <div>7-10 days</div>
                </div>
                
                <div className="table-row">
                  <div>0-6 days</div>
                  <div className="refund-none">0%</div>
                  <div>N/A</div>
                </div>
              </div>
            </div>
          </section>

          {/* Refund Process */}
          <section id="refund-process" className="policy-card">
            <div 
              className="card-header"
              onClick={() => isMobile && toggleSection('refund-process')}
            >
              <div className="header-left">
                <div className="card-icon">
                  <RiRefund2Line />
                </div>
                <h2>Refund Process</h2>
              </div>
              {isMobile && (
                <span className="expand-icon">
                  {expandedSections.includes('refund-process') ? <MdExpandLess /> : <MdExpandMore />}
                </span>
              )}
            </div>
            
            <div className={`card-body ${isMobile && !expandedSections.includes('refund-process') ? 'collapsed' : ''}`}>
              <div className="process-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-details">
                    <h5>Submit Request</h5>
                    <p>Email with booking ID</p>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-details">
                    <h5>Verification</h5>
                    <p>1-2 business days</p>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-details">
                    <h5>Processing</h5>
                    <p>3-5 business days</p>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">4</div>
                  <div className="step-details">
                    <h5>Confirmation</h5>
                    <p>Email notification</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="policy-card">
            <div 
              className="card-header"
              onClick={() => isMobile && toggleSection('contact')}
            >
              <div className="header-left">
                <div className="card-icon">
                  <MdContactSupport />
                </div>
                <h2>Contact Support</h2>
              </div>
              {isMobile && (
                <span className="expand-icon">
                  {expandedSections.includes('contact') ? <MdExpandLess /> : <MdExpandMore />}
                </span>
              )}
            </div>
            
            <div className={`card-body ${isMobile && !expandedSections.includes('contact') ? 'collapsed' : ''}`}>
              <div className="contact-info">
                <div className="contact-item">
                  <div className="contact-icon">
                    <MdEmail />
                  </div>
                  <div className="contact-details">
                    <h5>Email</h5>
                    <p>refunds@vnstours.com</p>
                    <span className="contact-note">24-hour response</span>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">
                    <MdPhone />
                  </div>
                  <div className="contact-details">
                    <h5>Phone</h5>
                    <p>+916307291567
</p>
                    <span className="contact-note">Mon-Fri, 10AM-6PM</span>
                  </div>
                </div>
              </div>
              
              <button className="action-btn primary">
                <MdEmail /> Email Request
              </button>
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="refund-footer">
          <div className="footer-content">
            <div className="footer-logo">
              <RiRefund2Line />
              <div>
                <h4>VNS Tours</h4>
                <p>Refund Policy</p>
              </div>
            </div>
            
            <div className="footer-text">
              <p>Policy effective from {lastUpdated}</p>
              <div className="footer-tags">
                <span>Fair Policy</span>
                <span>Quick Refunds</span>
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default RefundPolicy;